/**
 * CameraCommandsPanel — Cinematography presets organized into three
 * collapsible categories: Shot Types, Lighting, and Camera Moves.
 *
 * Appears below RichTextBox when motion prompt edit mode is active.
 * Each button inserts the preset tag into the active editor at the
 * caret position.
 *
 * Round 4:
 *  - Preset insertion no longer emits a closing `[End of …]` tag — only
 *    the opening `[Preset]` token is written into the prompt (item 5).
 *  - Consumers can pass a custom `onInsert` handler. The VideoGenerator
 *    Structured mode wires this to either (a) the open block editor's
 *    RichTextBox, or (b) a new video block when no editor is open
 *    (item 4). When `onInsert` is omitted, the panel falls back to the
 *    previous `richTextBoxRef.wrapSelection` behavior so other consumers
 *    keep working.
 */

import { memo, useCallback, useState } from 'react'
import type { RichTextBoxHandle } from './RichTextBox'

export const SHOT_TYPE_PRESETS: readonly string[] = [
  'Extreme Close-Up', 'Close-Up', 'Medium Close-Up', 'Medium Shot',
  'Medium Wide', 'Wide Shot', 'Extreme Wide', 'Over-The-Shoulder',
  'POV', 'Low Angle', 'High Angle', 'Dutch Angle',
]

export const LIGHTING_PRESETS: readonly string[] = [
  'Rembrandt', 'Butterfly', 'Split', 'Three-Point',
  'Rim Light', 'High Key', 'Low Key', 'Natural Light',
  'Silhouette', 'Golden Hour', 'Neon', 'Chiaroscuro',
]

export const CAMERA_COMMANDS: readonly string[] = [
  'Zoom In', 'Zoom Out', 'Pan Left', 'Pan Right', 'Tilt Up',
  'Tilt Down', 'Push In', 'Pull Out', 'Truck Left', 'Truck Right',
  'Pedestal Up', 'Pedestal Down', 'Shake', 'Tracking', 'Static',
]

interface PresetCategory {
  id: string
  label: string
  presets: readonly string[]
  columns: number
  testIdPrefix: string
}

const CATEGORIES: PresetCategory[] = [
  { id: 'shot-types', label: 'Shot Types', presets: SHOT_TYPE_PRESETS, columns: 4, testIdPrefix: 'shot-type' },
  { id: 'lighting', label: 'Lighting', presets: LIGHTING_PRESETS, columns: 4, testIdPrefix: 'lighting' },
  { id: 'camera-moves', label: 'Camera Moves', presets: CAMERA_COMMANDS, columns: 5, testIdPrefix: 'camera-cmd' },
]

interface CameraCommandsPanelProps {
  richTextBoxRef: React.RefObject<RichTextBoxHandle | null>
  /**
   * Custom insertion handler. When provided, takes over from the default
   * `richTextBoxRef.wrapSelection` path. Used by VideoGenerator Structured
   * mode to either insert into the open block editor or append a new video
   * block (item 4). When omitted, falls back to the default Freeform behavior.
   */
  onInsert?: (preset: string) => void
}

const ChevronIcon = memo(function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-2.5 h-2.5 text-text-muted/60 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
})

export const CameraCommandsPanel = memo(function CameraCommandsPanel({
  richTextBoxRef,
  onInsert,
}: CameraCommandsPanelProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  const toggleSection = useCallback((id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleCommandClick = useCallback(
    (command: string) => {
      // Item 4: custom handler wins. Item 5: only insert the opening tag,
      // never a `[End of …]` marker.
      if (onInsert) {
        onInsert(command)
        return
      }
      const handle = richTextBoxRef.current
      if (!handle) return
      // Item 5: insert `[Command]` only, no closing tag.
      handle.insertAtCursor(`[${command}] `)
    },
    [richTextBoxRef, onInsert],
  )

  return (
    <div data-testid="camera-commands-panel" className="space-y-1">
      {/* Top label with info tooltip */}
      <div className="flex items-center gap-1">
        <span className="text-xxs uppercase tracking-wider text-text-muted">
          Cinematography Presets
        </span>
        <span className="relative group/info">
          <svg
            className="w-3 h-3 text-text-muted/50 cursor-help"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="absolute bottom-full left-0 mb-1 px-2 py-1 rounded bg-black/90 text-[9px] text-white/80 whitespace-nowrap opacity-0 group-hover/info:opacity-100 transition-opacity duration-100 pointer-events-none z-50 shadow-lg">
            Click to insert cinematography tags into your prompt
          </span>
        </span>
      </div>

      {CATEGORIES.map((cat) => {
        const isOpen = !!openSections[cat.id]
        return (
          <div key={cat.id} data-testid={`preset-category-${cat.id}`}>
            <button
              type="button"
              className="flex items-center gap-1 w-full text-left py-0.5 group/cat"
              onMouseDown={(e) => {
                e.preventDefault()
                toggleSection(cat.id)
              }}
              data-testid={`preset-toggle-${cat.id}`}
            >
              <ChevronIcon open={isOpen} />
              <span className="text-[9px] font-medium text-text-muted group-hover/cat:text-text-secondary transition-colors">
                {cat.label}
              </span>
              <span className="text-[8px] text-text-muted/40 ml-0.5">
                {cat.presets.length}
              </span>
            </button>

            {isOpen && (
              <div
                className="grid gap-1 mt-0.5"
                style={{ gridTemplateColumns: `repeat(${cat.columns}, 1fr)` }}
              >
                {cat.presets.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleCommandClick(preset)
                    }}
                    className="px-1.5 py-1 text-[9px] rounded-full border border-border-subtle bg-surface-2 text-text-secondary hover:border-primary/40 hover:text-primary hover:bg-primary/10 transition-colors truncate"
                    title={preset}
                    data-testid={`${cat.testIdPrefix}-${preset.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})

CameraCommandsPanel.displayName = 'CameraCommandsPanel'
